import { useState, useDeferredValue, useMemo, useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";

export type FormData = {
  isAllSelected: boolean;
  selectedLeagues: string[];
};

export type LeagueFilterSharedProps = {
  onSubmit?: (data: FormData) => void;
  options?: { label: string; value: string; favorite?: boolean }[];
  defaultIsAllSelected?: boolean;
  defaultSelectedLeagues?: string[];
};

export function useLeagueFilterForm({
  onSubmit = () => {},
  options = [],
  defaultIsAllSelected = true,
  defaultSelectedLeagues = [],
}: LeagueFilterSharedProps) {
  const { handleSubmit, control, setValue } = useForm<FormData>({
    defaultValues: {
      isAllSelected: defaultIsAllSelected,
      selectedLeagues: defaultSelectedLeagues,
    },
  });

  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  const selectedLeagues = useWatch({ control, name: "selectedLeagues" });
  const isAllSelected = useWatch({ control, name: "isAllSelected" });

  const length = options.length;

  const selectedLeaguesNo = useMemo(
    () => (isAllSelected ? length - selectedLeagues.length : selectedLeagues.length),
    [isAllSelected, selectedLeagues, length]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    []
  );

  const handleToggleAll = useCallback(
    (checked: boolean, onChange: (val: boolean) => void) => {
      setValue("selectedLeagues", []);
      onChange(checked);
    },
    [setValue]
  );

  const handleToggleLeague = useCallback(
    (leagueId: string, isChecked: boolean, onChange: (val: string[]) => void, current: string[]) => {
      if (isChecked) {
        onChange(current.filter((league) => league !== leagueId));
      } else {
        onChange([...current, leagueId]);
      }
    },
    []
  );

  const filteredOptions = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, deferredSearch]);

  return {
    control,
    handleSubmit,
    onSubmit,
    search,
    handleSearchChange,
    filteredOptions,
    isAllSelected,
    selectedLeaguesNo,
    length,
    handleToggleAll,
    handleToggleLeague,
  };
}
